import { Analytics } from '@analytics';
import { Button, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import * as yup from 'yup';
import { PaymentOptionTypes } from './constants';
import messages from './messages';

const useStyles = makeStyles(() => ({
  textField: {
    maxWidth: '120px',
    fontSize: '13px',
    '& .MuiInputBase-input': {
      height: '40px',
      padding: '5px 14px',
      maxWidth: '120px'
    }
  },
  bespokeInput: {
    width: '100%',
    fontSize: '13px',
    '& .MuiInputBase-input': {
      height: '40px',
      padding: '5px 14px',
      width: '100%'
    }
  },
  radioButtonLabel: {
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    justifyContent: 'center',
    display: 'flex',
    alignItems: 'center',
    '& input': {
      display: 'block',
      position: 'absolute',
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      opacity: '0.01',
      zIndex: '100',
      margin: '0',
      cursor: 'pointer'
    }
  }
}));

const tipValueSchema = yup.object({
  paymentOptionEnabled: yup.string().required(),
  defaultPaymentAmount: yup.number()
});

const PaymentOptionsSwitcher = ({
  mergedProfile,
  handleSubmit,
  validateSchema,
  asForm = false,
  enableSwitching = true
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(mergedProfile.paymentOptions.paymentOptionEnabled);

  const handleTabSwitch = paymentOption => {
    Analytics.trackViewFeature('ChangeTippingTypes');
    setActiveTab(paymentOption);
  };

  const handleBlockExponent = evt => evt.key === 'e' && evt.preventDefault();

  const getTabInfo = tab => {
    switch (tab) {
      case PaymentOptionTypes.quick:
        return (
          <div className="flex flex-row justify-between mt-8 gap-3">
            {Object.entries(mergedProfile.paymentOptions.quickPaymentAmounts).map(([key]) => (
              <Typography key={key}>
                <Field name={`paymentOptions.quickPaymentAmounts.${key}`}>
                  {({ field }) => (
                    <TextField
                      className={classes.textField}
                      label={intl.formatMessage(messages[key])}
                      onKeyDown={handleBlockExponent}
                      variant="outlined"
                      type="number"
                      required
                      InputProps={{
                        inputProps: {
                          min: 2,
                          step: 0.1
                        }
                      }}
                      {...field}
                    />
                  )}
                </Field>
                <Typography className="!text-red-600 !text-[10px] !mt-1 max-w-[120px]">
                  <ErrorMessage name={`paymentOptions.quickPaymentAmounts.${key}`} />
                </Typography>
              </Typography>
            ))}
          </div>
        );
      case PaymentOptionTypes.bespoke:
        return (
          <div className="mt-8 max-w-full">
            <Typography>
              <Field name={'paymentOptions.defaultPaymentAmount'}>
                {({ field }) => (
                  <TextField
                    className={classes.bespokeInput}
                    label={intl.formatMessage(messages.defaultPaymentAmountLabel)}
                    onKeyDown={handleBlockExponent}
                    variant="outlined"
                    type="number"
                    required
                    InputProps={{
                      inputProps: {
                        min: 2,
                        step: 0.1
                      }
                    }}
                    {...field}
                  />
                )}
              </Field>
              <Typography className="!text-red-600 !text-[10px] !mt-1">
                <ErrorMessage name={`paymentOptions.defaultPaymentAmount`} />
              </Typography>
            </Typography>
          </div>
        );
      default:
        return null;
    }
  };

  const getTabText = tab => {
    switch (tab) {
      case PaymentOptionTypes.quick:
        return {
          header: intl.formatMessage(messages.quickTipAmounts),
          details: intl.formatMessage(messages.letCustomersKnowQuick)
        };
      case PaymentOptionTypes.bespoke:
        return {
          header: intl.formatMessage(messages.bespokeAmount),
          details: intl.formatMessage(messages.letCustomersKnowBespoke)
        };
      default:
        return null;
    }
  };

  const handleShowTab = tab => {
    const tabInfo = getTabInfo(tab);
    const tabText = getTabText(tab);

    if (!tabInfo || !tabText) {
      console.error('Unknown tab type');
      return null;
    }

    return (
      <>
        <div>
          <div className="text-2xl font-bold text-slate-800 tracking-tight">{tabText.header}</div>
          <div className="text-sm font-medium text-slate-400">{tabText.details}</div>
        </div>
        <div className="w-full">{tabInfo}</div>

        {tab === PaymentOptionTypes.quick && (
          <div className="text-[10px] text-slate-600 font-semibold text-center mt-1">
            {intl.formatMessage(messages.preselectedAmount)}
          </div>
        )}
      </>
    );
  };

  const getButtonSwitcher = () => {
    if (!enableSwitching) return;

    const switcherTabClasses =
      'w-1/2 border-0 text-[10px] cursor-pointer font-semibold rounded-lg relative transition-colors duration-150';

    return (
      <div className="flex flex-row bg-slate-100 border border-solid border-slate-200 h-12 rounded-xl p-1 mb-3.5" role="group">
        <button
          value={PaymentOptionTypes.quick}
          type="button"
          className={`${switcherTabClasses} ${
            activeTab === PaymentOptionTypes.quick
              ? 'bg-white text-slate-900 shadow-sm'
              : 'bg-transparent text-slate-500 hover:bg-white'
          }`}
          onClick={() => {
            handleTabSwitch(PaymentOptionTypes.quick);
          }}>
          <label className={classes.radioButtonLabel}>
            <Field
              type="radio"
              name={'paymentOptions.paymentOptionEnabled'}
              value={PaymentOptionTypes.quick}
            />
            {intl.formatMessage(messages.quickTipAmounts)}
          </label>
        </button>
        <button
          value={PaymentOptionTypes.bespoke}
          type="button"
          className={`${switcherTabClasses} ${
            activeTab === PaymentOptionTypes.bespoke
              ? 'bg-white text-slate-900 shadow-sm'
              : 'bg-transparent text-slate-500 hover:bg-white'
          }`}
          onClick={() => {
            handleTabSwitch(PaymentOptionTypes.bespoke);
          }}>
          <label className={classes.radioButtonLabel}>
            <Field
              type="radio"
              name={'paymentOptions.paymentOptionEnabled'}
              value={PaymentOptionTypes.bespoke}
            />
            {intl.formatMessage(messages.bespokeAmount)}
          </label>
        </button>
      </div>
    );
  };

  // use component as a part of existing form
  if (!asForm || !handleSubmit) {
    return (
      <div className="w-full h-auto">
        {getButtonSwitcher()}
        {handleShowTab(activeTab)}
      </div>
    );
  }

  // use component as a standalone form
  return (
    <div className="w-full h-auto">
      <Formik
        initialValues={mergedProfile}
        validateOnChange={false}
        validate={validateSchema(tipValueSchema)}
        validateOnBlur={false}
        onSubmit={handleSubmit}>
        <Form>
          {getButtonSwitcher()}
          {handleShowTab(activeTab)}

          <Button
            type="submit"
            className="w-full !bg-indigo-600 hover:!bg-indigo-700 !text-white !font-semibold !px-5 !py-2.5 !mt-2.5 !mb-4 !rounded-xl !shadow-sm !normal-case">
            {intl.formatMessage(messages.saveChanges)}
          </Button>
        </Form>
      </Formik>
    </div>
  );
};

export default PaymentOptionsSwitcher;
